import { notification } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { getCompanyId } from "@lhd/utility";
import { Tasks } from "@lhd/types";
import { pick } from "lodash";
const API_V2 = process.env.REACT_APP_API_URL_V2;

export const useFetchTasks = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.TASKS, companyId, page, pageSize, filterParams],
    () =>
      axios({
        method: "get",
        url: SERVICES.TASKS,
        params: {
          page,
          per_page: pageSize,
          ...filterParams,
          "q[company_id_eq]": companyId,
        },
      }).then((res) => ({
        data: res.data as Tasks[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};

export const useFetchTasksByProject = (
  projectId: string | number | null | undefined,
  startDate?: string,
  endDate?: string,
  userId?: string | number | null,
  byStartAt = "asc",
) => {
  const companyId = getCompanyId();

  const params: any = {
    "q[project_id_eq]": projectId,
    "filter_range_date[start_date]": startDate,
    "filter_range_date[end_date]": endDate,
    per_page: 10000,
    "q[company_id_eq]": companyId,
    by_start_at: byStartAt,
  };

  if (userId) {
    params.filter_by_user_id = userId;
  }

  return useQuery(
    [
      QUERY_NAME.TASKS,
      companyId,
      projectId,
      startDate,
      endDate,
      userId,
      byStartAt,
    ],
    () =>
      axios({
        method: "get",
        baseURL: API_V2,
        url: SERVICES.TASKS,
        params,
      }).then((res) => ({
        data: res.data as Tasks[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
  );
};

export const useFetchOneTask = (taskId: any) => {
  return useQuery(
    [QUERY_NAME.TASK, taskId],
    () =>
      axios({
        method: "get",
        baseURL: API_V2,
        url: `${SERVICES.TASKS}/${taskId}`,
      }).then((res) => res.data as Tasks),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!taskId,
    },
  );
};

export const useSubmitTask = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, taskId, files = [] }: any) => {
      const formData = new FormData();

      const dataSubmit: any = pick(payload, [
        "company_id",
        "project_id",
        "title",
        "start_at",
        "status",
        "content",
        "worker",
        "parent_task_id",
        "end_at",
        "achieve_start_at",
        "achieve_end_at",
        "progress",
        "task_log_comment",
        "attachment_deleted_ids",
      ]);
      Object.keys(dataSubmit).forEach((keyItem: keyof typeof dataSubmit) => {
        if (
          ["achieve_start_at", "achieve_end_at", "end_at"].includes(
            String(keyItem),
          ) &&
          !dataSubmit?.[keyItem]
          // eslint-disable-next-line no-empty
        ) {
        } else if (keyItem === "attachment_deleted_ids") {
          dataSubmit.attachment_deleted_ids.forEach((x: number) => {
            formData.append("attachment_deleted_ids[]", x + "");
          });
        } else {
          formData.append(keyItem as string, dataSubmit[keyItem]);
        }
      });

      files.forEach(({ file, description = "" }: any) => {
        formData.append(`attachments[][file]`, file, file?.name);
        formData.append(`attachments[][description]`, description);
      });

      return axios({
        method: taskId ? "patch" : "post",
        url: taskId ? `${SERVICES.TASKS}/${taskId}` : SERVICES.TASKS,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.TASKS]);
        queryClient.invalidateQueries([QUERY_NAME.TASK]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(e: any) {
        let messageError = "";
        const listErrors = e?.response?.data?.errors;
        Object.keys(listErrors).forEach((keyItem) => {
          if (
            listErrors[keyItem] &&
            Array.isArray(listErrors[keyItem]) &&
            listErrors[keyItem]?.length > 0
          ) {
            messageError = listErrors[keyItem]?.[0];
          }
        });

        if (messageError) {
          notification["error"]({
            message: messageError,
          });
        }
      },
    },
  );
};

export const useUpdateLogTask = () => {
  // change history
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload, id, onSuccess }: any) => {
      return axios({
        method: "patch",
        url: `${SERVICES.TASK_LOG_DETAILS}/${id}`,
        data: payload,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(_, variables) {
        if (variables.onSuccess) {
          variables.onSuccess();
        }
        queryClient.invalidateQueries([QUERY_NAME.TASKS]);
        queryClient.invalidateQueries([QUERY_NAME.TASK]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(e: any) {
        let messageError = "";
        const listErrors = e?.response?.data?.errors;
        Object.keys(listErrors).forEach((keyItem) => {
          if (
            listErrors[keyItem] &&
            Array.isArray(listErrors[keyItem]) &&
            listErrors[keyItem]?.length > 0
          ) {
            messageError = listErrors[keyItem]?.[0];
          }
        });

        if (messageError) {
          notification["error"]({
            message: messageError,
          });
        } else {
          notification["error"]({
            message: "無効なデータ",
          });
        }
      },
    },
  );
};

export const useSubmitSaveTasks = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.TASKS_SAVE,
        data: payload,
      }),
    {
      onSuccess(e) {
        queryClient.invalidateQueries([QUERY_NAME.TASKS]);
        if (e?.data?.errors?.length > 0) {
          notification["error"]({
            message: e?.data?.errors?.[0],
          });
        } else {
          // notification["success"]({
          //   message: "保存しました",
          // });
        }
      },
    },
  );
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ taskId }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.TASKS}/${taskId}`,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.TASKS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteMultipleTask = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listTaskIds }: any) => {
      const formData = new FormData();
      listTaskIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.TASKS}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.TASKS]);
        notification["success"]({
          message: "保存しました",
        });
        onSuccess && onSuccess();
      },
    },
  );
};
